import React, { useEffect, useState } from "react";
//import "./orderdetails.css";
import { useQuery } from "@tanstack/react-query";
import { API_URL } from "../../../services/apiConfig";
import { useNavigate, useSearchParams } from "react-router-dom";
import { capitalize } from "../../../utils/stringUtils";
import axiosInstance from "../../../services/axios.instance";
import { ImagePopupModal } from "../../../components";

const VendorServiceDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const OrderId = searchParams.get("orderId");
  const page = searchParams.get("page") || "1";
  const size = searchParams.get("size") || "10";
  const [showImagePopupModal, setShowImagePopupModal] = useState(false);
  const [imageSource, setImageSource] = useState("");
  const accessToken = localStorage.getItem("access_token");

  const decodeToken = (token: any) => {
    if (token) {
      return JSON.parse(atob(token.split(".")[1]));
    } else {
      return null;
    }
  };

  const decodedToken = decodeToken(accessToken);
  const [isAAAUser, setIsAAAUser] = useState(false);
  const [isAFLUser, setIsAFLUser] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    const decoded = decodeToken(token);
    const roles = decoded?.roles || [];
    if (roles.includes("ROLE_AAA_USER")) {
      setIsAAAUser(true);
    }
    if (roles.includes("ROLE_AFL_USER")) {
      setIsAFLUser(true);
    }
  }, []);

  const handleBack = () => {
    navigate({
      pathname: "/vendor/dashboard/servicelist",
      search: `?page=${page}&size=${size}`,
    });
  };
  //const { orderDetails } = location.state;

  interface DeliveryPartnerLocation {
    longitude: any;
    latitude: any;
    location: string;
    order_status: string;
    delivery_partner_status: string;
    changed_at: string;
    distance_in_km: number;
  }

  const { data: orderDetails } = useQuery({
    queryKey: ["orderDetails", OrderId],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${API_URL.SERVICE_DETAILS}/${OrderId}`,
        {
          headers: {
            location: "0,0",
            language: "english",
          },
        }
      );
      return response.data;
    },
  });

  const formatDate = (isoDate: any) => {
    const date = new Date(isoDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };
  //--------------------------------------------------------------------------
  const driverReachedTimeDifference = (createdAt: string) => {
    const createdDate = new Date(createdAt);
    const driverReachedTime =
      orderDetails?.order_status_history_responses?.find(
        (status: any) =>
          status.delivery_partner_status === "REACHED_CUSTOMER_LOCATION"
      )?.status_changed_at;

    if (!driverReachedTime) return { totalMinutes: 0, formatted: "N/A" };

    const driverReachedDate = new Date(driverReachedTime);
    const driverDiffInSeconds = Math.floor(
      (driverReachedDate.getTime() - createdDate.getTime()) / 1000
    );

    const hours = Math.floor(driverDiffInSeconds / 3600);
    const minutes = Math.floor((driverDiffInSeconds % 3600) / 60);
    const totalMinutes = hours * 60 + minutes;

    return { totalMinutes, formatted: formatTime(hours, minutes) };
  };

  const timeDifference = (createdAt: string) => {
    const now = new Date();
    const createdDate = new Date(createdAt);
    const diffInSeconds = Math.floor(
      (now.getTime() - createdDate.getTime()) / 1000
    );

    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const totalMinutes = hours * 60 + minutes;

    return { totalMinutes, formatted: formatTime(hours, minutes) };
  };

  const formatTime = (hours: number, minutes: number) => {
    if (hours > 0 && minutes > 0) {
      return `${hours} hour(s) ${minutes} minute(s) ago`;
    } else if (hours > 0) {
      return `${hours} hour(s) ago`;
    } else if (minutes > 0) {
      return `${minutes} minute(s) ago`;
    } else {
      return "Just now";
    }
  };
  // const { totalMinutes, formatted } = timeDifference(orderDetails?.createdAt);
  // const statusColor = totalMinutes < 100 ? "text-success" : "text-danger";
  const shouldUseTimeDifference =
    orderDetails?.aaa_service_request_model?.state === "initiate";
  const timeData = shouldUseTimeDifference
    ? timeDifference(orderDetails?.createdAt)
    : { totalMinutes: 0, formatted: "Just now" };

  // Determine status color based on conditions
  const statusColor = shouldUseTimeDifference
    ? timeData.totalMinutes < 10
      ? "text-success"
      : "text-danger"
    : orderDetails?.is_dispatched_within_time
    ? "text-success"
    : "text-danger";

  const { totalMinutes: totalMinutesDriverReached } =
    driverReachedTimeDifference(orderDetails?.createdAt);

  const driverReachedStatusColor =
    totalMinutesDriverReached < 60 ? "text-success" : "text-danger";

  const downloadAllChecklistImages = async () => {
    interface ImageItem {
      url: string;
      type: "checklist" | "recollected";
      originalIndex: number;
    }

    const checklistImages = (
      orderDetails?.products[0]?.checkListImageUrls || []
    )
      .map(
        (url: string, index: number): ImageItem => ({
          url,
          type: "checklist",
          originalIndex: index,
        })
      )
      .filter((item: ImageItem) => Boolean(item.url));

    const recollectedImages = (
      orderDetails?.products[0]?.recollectedCheckListImageUrls || []
    )
      .map(
        (url: string, index: number): ImageItem => ({
          url,
          type: "recollected",
          originalIndex: index,
        })
      )
      .filter((item: ImageItem) => Boolean(item.url));

    const allImages = [...checklistImages, ...recollectedImages];

    const downloadFile = (
      url: string,
      originalIndex: number,
      type: string
    ): Promise<void> => {
      return new Promise((resolve) => {
        try {
          const fileExtension = url.endsWith(".pdf") ? ".pdf" : ".jpg";
          const fileName = `${
            type === "checklist" ? "Checklist" : "Recollected"
          }_${originalIndex + 1}${fileExtension}`;

          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();

          setTimeout(() => {
            document.body.removeChild(link);
            resolve();
          }, 1000); // 1s delay to allow download to start
        } catch (error) {
          console.error(`Error downloading file: ${error}`);
          resolve();
        }
      });
    };

    for (const item of allImages) {
      if (item.url) {
        await downloadFile(item.url, item.originalIndex, item.type);
      }
    }
  };

  const onClickVdrDocument = (imageUrl: string) => {
    setImageSource(imageUrl);
    setShowImagePopupModal(true);
  };
  const { data: deliveryPartnerData = [] } = useQuery<
    DeliveryPartnerLocation[]
  >({
    queryKey: [
      "deliveryPartnerLocation",
      orderDetails?.deliveryPartnerId,
      orderDetails?.order_service_details?.orderNumber,
    ],
    queryFn: async () => {
      if (!orderDetails?.order_service_details?.orderNumber) {
        return [];
      }

      const payload = orderDetails?.deliveryPartnerId
        ? {
            delivery_partner_id: orderDetails.deliveryPartnerId,
            order_number: orderDetails.order_service_details.orderNumber,
          }
        : {
            service_from_app: "false",
            order_number: orderDetails.order_service_details.orderNumber,
          };

      const response = await axiosInstance.post(
        `${API_URL.DRIVER_LOCATION}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data || [];
    },
    enabled:
      // !!orderDetails?.deliveryPartnerId &&
      !!orderDetails?.order_service_details?.orderNumber,
  });

  return (
    <div>
      <div className="col-12">
        <div className="row">
          <div className="col-md-12 text-start div-bg-invoice-details pt-3">
            <div className="card card-bg-invoice-details">
              <div className="card-body px-5">
                <div>
                  <button onClick={handleBack} className="all_button_style">
                    Back
                  </button>
                </div>
                <div className="row pb-3">
                  <div className="table-responsive pt-3">
                    {/* <div className="mb-3 text-center invoice-text-yellow-afl">
                      <h4>
                        Job Reference No:{" "}
                        <span className="">
                          {orderDetails?.order_service_details?.referenceNumber}
                        </span>
                      </h4>
                    </div> */}
                    <div className="row pt-3">
                      {/* LEFT SIDE TABLE */}
                      <div className="col-md-6">
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <tbody>
                              <tr>
                                <th>Vehicle Make</th>
                                <td>
                                  {capitalize(
                                    orderDetails?.order_service_details
                                      ?.vehicleMake || ""
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <th>Vehicle Model</th>
                                <td>
                                  {capitalize(
                                    orderDetails?.order_service_details
                                      ?.carModel || ""
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <th>Mobile</th>
                                <td>
                                  {
                                    orderDetails?.aaa_service_request_model
                                      ?.mobileNumber
                                  }
                                </td>
                              </tr>
                              <tr>
                                <th>Plate Number</th>
                                <td>
                                  {
                                    orderDetails?.aaa_service_request_model
                                      ?.plateNumber
                                  }
                                </td>
                              </tr>
                              <tr>
                                <th>Chassis Number</th>
                                <td>{orderDetails?.chassis_number}</td>
                              </tr>
                              <tr>
                                <th>Service Date</th>
                                <td>{formatDate(orderDetails?.createdAt)}</td>
                              </tr>

                              {orderDetails?.recollection_location ? (
                                <>
                                  <tr>
                                    <th>Location From</th>
                                    <td>
                                      {orderDetails?.delivery_location?.address}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th>Location To</th>
                                    <td>
                                      {
                                        orderDetails?.recollection_location
                                          ?.address
                                      }
                                    </td>
                                  </tr>
                                </>
                              ) : (
                                <tr>
                                  <th>Location To</th>
                                  <td>
                                    {orderDetails?.delivery_location?.address}
                                  </td>
                                </tr>
                              )}

                              <tr>
                                <th>Comments AFL</th>
                                <td>
                                  {
                                    orderDetails?.order_service_details
                                      ?.aflComments
                                  }
                                </td>
                              </tr>

                              {orderDetails?.merchant_comment && (
                                <tr>
                                  <th>Comments</th>
                                  <td>{orderDetails?.merchant_comment}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* RIGHT SIDE TABLE */}
                      <div className="col-md-6">
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <tbody>
                              <tr>
                                <th>Customer Name</th>
                                <td>{orderDetails?.customer_name}</td>
                              </tr>
                              <tr>
                                <th>Service Type</th>
                                <td>
                                  {capitalize(
                                    orderDetails?.order_service_details
                                      ?.serviceName || ""
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <th>Service Status</th>
                                <td>
                                  {(() => {
                                    const state =
                                      orderDetails?.aaa_service_request_model?.state?.toLowerCase();
                                    const isDriverReachedAndCancelled =
                                      orderDetails?.order_service_details
                                        ?.isDriverReachedAndCancelled;

                                    // Handle AFL user cases
                                    if (
                                      isAFLUser &&
                                      state === "driver_cancel"
                                    ) {
                                      return isDriverReachedAndCancelled
                                        ? "R&C"
                                        : "Cancelled";
                                    }

                                    // Handle reached and cancelled cases for non-AFL users
                                    if (isDriverReachedAndCancelled) {
                                      if (state === "cancel") {
                                        return "R&C";
                                      }
                                    }

                                    // For non-AFL users, show 'Driver Cancel Pending' when state is 'driver_cancel'
                                    if (
                                      !isAFLUser &&
                                      state === "driver_cancel"
                                    ) {
                                      return "Driver Cancel Pending";
                                    }

                                    // For AFL users, show 'Cancelled' for other cancelled states
                                    if (
                                      isAFLUser &&
                                      (state === "cancel" ||
                                        state === "reached_and_cancelled")
                                    ) {
                                      return "Cancelled";
                                    }

                                    // Handle completed_by_driver state
                                    if (state === "completed_by_driver") {
                                      return "Completed";
                                    }

                                    // Default case - return the state as is
                                    return state
                                      ? state.charAt(0).toUpperCase() +
                                          state.slice(1)
                                      : "";
                                  })()}
                                </td>
                              </tr>
                              <tr>
                                <th>Scheduled Date & Time (if any)</th>
                                <td>
                                  {formatDate(
                                    orderDetails?.order_service_details
                                      ?.scheduledFor
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <th>Driver Name</th>
                                <td style={{ maxWidth: "100px" }}>
                                  {`${
                                    orderDetails?.aaa_service_request_model
                                      ?.driverName ??
                                    `${
                                      orderDetails?.delivery_boy_details
                                        ?.firstName || ""
                                    } ${
                                      orderDetails?.delivery_boy_details
                                        ?.lastName || ""
                                    }`
                                  }`.trim()}
                                </td>
                              </tr>
                              <tr>
                                <th>Driver Contact</th>
                                <td>
                                  {orderDetails?.aaa_service_request_model
                                    ?.driverPhone ??
                                    orderDetails?.delivery_boy_details
                                      ?.phoneNumber}
                                </td>
                              </tr>
                              {!isAFLUser && (
                                <tr>
                                  <th>Comments from AAA agent</th>
                                  <td>
                                    {orderDetails?.aaa_service_request_model
                                      ?.comments?.length > 0 ? (
                                      orderDetails?.aaa_service_request_model?.comments.map(
                                        (comment: any, index: number) => (
                                          <div key={index}>
                                            {comment?.comment}
                                            <br />
                                          </div>
                                        )
                                      )
                                    ) : (
                                      <span>No comments available</span>
                                    )}
                                  </td>
                                </tr>
                              )}
                              <tr>
                                <th>Initiated DT</th>
                                <td>
                                  {formatDate(orderDetails?.createdAt)}
                                  <span className="text-muted">
                                    <span className={statusColor}>
                                      {" "}
                                      : STATUS
                                    </span>
                                  </span>
                                </td>
                              </tr>
                              <tr>
                                <th>Driver Started DT</th>
                                <td>
                                  {orderDetails?.transferred_order_timeline?.[
                                    "Driver Started DT"
                                  ]
                                    ? formatDate(
                                        orderDetails.transferred_order_timeline[
                                          "Driver Started DT"
                                        ]
                                      )
                                    : orderDetails?.order_status_history_responses?.some(
                                        (status: any) =>
                                          status.delivery_partner_status ===
                                          "DELIVERY_ACCEPTED"
                                      )
                                    ? formatDate(
                                        orderDetails?.order_status_history_responses?.find(
                                          (status: any) =>
                                            status.delivery_partner_status ===
                                            "DELIVERY_ACCEPTED"
                                        )?.status_changed_at
                                      )
                                    : "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <th>Driver Reached DT</th>
                                <td>
                                  {orderDetails?.transferred_order_timeline?.[
                                    "Driver Reached DT"
                                  ] ? (
                                    <>
                                      {formatDate(
                                        orderDetails.transferred_order_timeline[
                                          "Driver Reached DT"
                                        ]
                                      )}
                                      <span className="text-muted">
                                        <span
                                          className={driverReachedStatusColor}
                                        >
                                          {" "}
                                          : STATUS
                                        </span>
                                      </span>
                                    </>
                                  ) : orderDetails?.order_status_history_responses?.some(
                                      (status: any) =>
                                        status.delivery_partner_status ===
                                        "REACHED_CUSTOMER_LOCATION"
                                    ) ? (
                                    <>
                                      {formatDate(
                                        orderDetails?.order_status_history_responses?.find(
                                          (status: any) =>
                                            status.delivery_partner_status ===
                                            "REACHED_CUSTOMER_LOCATION"
                                        )?.status_changed_at
                                      )}
                                      <span className="text-muted">
                                        <span
                                          className={driverReachedStatusColor}
                                        >
                                          {" "}
                                          : STATUS
                                        </span>
                                      </span>
                                    </>
                                  ) : (
                                    "N/A"
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <th>Service Started DT</th>
                                <td>
                                  {orderDetails?.transferred_order_timeline?.[
                                    "Service Started DT"
                                  ]
                                    ? formatDate(
                                        orderDetails.transferred_order_timeline[
                                          "Service Started DT"
                                        ]
                                      )
                                    : orderDetails?.order_status_history_responses?.some(
                                        (status: any) =>
                                          status.delivery_partner_status ===
                                          (orderDetails?.order_service_details
                                            ?.serviceType === "DISTANCE_BASED"
                                            ? "VEHICLE_COLLECTED"
                                            : "SERVICE_STARTED")
                                      )
                                    ? formatDate(
                                        orderDetails?.order_status_history_responses?.find(
                                          (status: any) =>
                                            status.delivery_partner_status ===
                                            (orderDetails?.order_service_details
                                              ?.serviceType === "DISTANCE_BASED"
                                              ? "VEHICLE_COLLECTED"
                                              : "SERVICE_STARTED")
                                        )?.status_changed_at
                                      )
                                    : "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <th>Service Completed DT</th>
                                <td>
                                  {orderDetails?.order_status_history_responses?.some(
                                    (status: any) =>
                                      status.delivery_partner_status ===
                                      "COMPLETED"
                                  )
                                    ? formatDate(
                                        orderDetails?.order_status_history_responses?.find(
                                          (status: any) =>
                                            status.delivery_partner_status ===
                                            "COMPLETED"
                                        )?.status_changed_at
                                      )
                                    : "N/A"}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-3">
                    {/* Checklist Section */}
                    {(orderDetails?.products[0]?.checkListImageUrls?.length >
                      0 ||
                      orderDetails?.transferred_order_details?.[0]
                        ?.checkListImageUrls?.length > 0) && (
                      <>
                        <div className="col-md-12 invoice-div-data-afl mb-2">
                          Checklist
                        </div>
                        <div className="row g-2">
                          {/* Original Checklist Images */}
                          {orderDetails?.products[0]?.checkListImageUrls?.map(
                            (url: string | undefined, index: number) => (
                              <div
                                className="col-md-2 col-sm-4 col-6"
                                key={`original-${index}`}
                              >
                                <div
                                  className="card shadow-sm p-2 text-center"
                                  style={{ minHeight: 120 }}
                                >
                                  {url?.endsWith(".pdf") ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                                      <i className="bi bi-file-earmark-pdf text-danger fs-3"></i>
                                      <a
                                        href={url}
                                        download={`Checklist ${
                                          (index as number) + 1
                                        }.pdf`}
                                        className="mt-1 small text-decoration-none"
                                      >
                                        PDF {(index as number) + 1}
                                      </a>
                                    </div>
                                  ) : (
                                    <>
                                      <img
                                        src={url}
                                        onClick={() => onClickVdrDocument(url!)}
                                        alt={`Checklist ${
                                          (index as number) + 1
                                        }`}
                                        className="img-fluid rounded"
                                        style={{
                                          maxHeight: 80,
                                          objectFit: "cover",
                                          cursor: "pointer",
                                        }}
                                      />
                                      <a
                                        href={url}
                                        download={`Checklist ${
                                          (index as number) + 1
                                        }.jpg`}
                                        className="mt-1 d-block small text-decoration-none"
                                      >
                                        Download
                                      </a>
                                    </>
                                  )}
                                </div>
                              </div>
                            )
                          )}

                          {/* Transferred Order Checklist Images */}
                          {orderDetails?.transferred_order_details?.[0]?.checkListImageUrls?.map(
                            (url: string | undefined, index: number) => (
                              <div
                                className="col-md-2 col-sm-4 col-6"
                                key={`transferred-${index}`}
                              >
                                <div
                                  className="card shadow-sm p-2 text-center"
                                  style={{ minHeight: 120 }}
                                >
                                  {url?.endsWith(".pdf") ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                                      <i className="bi bi-file-earmark-pdf text-danger fs-3"></i>
                                      <a
                                        href={url}
                                        download={`Transferred_Checklist_${
                                          (index as number) + 1
                                        }.pdf`}
                                        className="mt-1 small text-decoration-none"
                                      >
                                        Transferred PDF {(index as number) + 1}
                                      </a>
                                    </div>
                                  ) : (
                                    <>
                                      <img
                                        src={url}
                                        onClick={() => onClickVdrDocument(url!)}
                                        alt={`Transferred Checklist ${
                                          (index as number) + 1
                                        }`}
                                        className="img-fluid rounded"
                                        style={{
                                          maxHeight: 80,
                                          objectFit: "cover",
                                          cursor: "pointer",
                                        }}
                                      />
                                      <a
                                        href={url}
                                        download={`Transferred_Checklist_${
                                          (index as number) + 1
                                        }.jpg`}
                                        className="mt-1 d-block small text-decoration-none"
                                      >
                                        Download
                                      </a>
                                    </>
                                  )}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </>
                    )}

                    {/* Recollected Checklist Section */}
                    {orderDetails?.products[0]?.recollectedCheckListImageUrls
                      ?.length > 0 && (
                      <>
                        <div className="col-md-12 invoice-div-data-afl mt-3 mb-2">
                          Recollected Checklist
                        </div>
                        <div className="row g-2">
                          {orderDetails?.products[0]?.recollectedCheckListImageUrls?.map(
                            (url: string | undefined, index: number) => (
                              <div
                                className="col-md-2 col-sm-4 col-6"
                                key={index}
                              >
                                <div
                                  className="card shadow-sm p-2 text-center"
                                  style={{ minHeight: 120 }}
                                >
                                  {url?.endsWith(".pdf") ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                                      <i className="bi bi-file-earmark-pdf text-danger fs-3"></i>
                                      <a
                                        href={url}
                                        download={`Recollected Checklist ${
                                          (index as number) + 1
                                        }.pdf`}
                                        className="mt-1 small text-decoration-none"
                                      >
                                        PDF {(index as number) + 1}
                                      </a>
                                    </div>
                                  ) : (
                                    <>
                                      <img
                                        src={url}
                                        onClick={() => onClickVdrDocument(url!)}
                                        alt={`Recollected Checklist ${
                                          (index as number) + 1
                                        }`}
                                        className="img-fluid rounded"
                                        style={{
                                          maxHeight: 80,
                                          objectFit: "cover",
                                          cursor: "pointer",
                                        }}
                                      />
                                      <a
                                        href={url}
                                        download={`Recollected Checklist ${
                                          (index as number) + 1
                                        }.jpg`}
                                        className="mt-1 d-block small text-decoration-none"
                                      >
                                        Download
                                      </a>
                                    </>
                                  )}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  <div>
                    {(orderDetails?.products[0]?.checkListImageUrls?.length >
                      0 ||
                      orderDetails?.products[0]?.recollectedCheckListImageUrls
                        ?.length > 0) && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginTop: "1rem",
                        }}
                      >
                        <button
                          className="invoice-approve-btn"
                          onClick={downloadAllChecklistImages}
                        >
                          Download All Images
                        </button>
                      </div>
                    )}
                  </div>
                  {orderDetails?.orderStatus === "COMPLETED" &&
                    orderDetails?.order_service_details?.tripSheetUrl && (
                      <>
                        <div className="col-md-6 pt-3">
                          <a
                            href={
                              orderDetails.order_service_details.tripSheetUrl
                            }
                            download="Tripsheet.pdf"
                            className="invoice-approve-btn"
                            style={{
                              display: "inline-block",
                              padding: "8px 16px",
                            }}
                          >
                            Download Tripsheet
                          </a>
                        </div>
                      </>
                    )}
                  {/* Delivery Partner Status Table */}
                  {deliveryPartnerData.length > 0 && (
                    <div className="col-md-12 pt-4">
                      <div className="invoice-div-head-afl">
                        Delivery Partner Status History
                      </div>
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>Partner Status</th>
                              <th>Location</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deliveryPartnerData
                              .filter(
                                (item) =>
                                  item.delivery_partner_status ===
                                    "REACHED_CUSTOMER_LOCATION" ||
                                  item.delivery_partner_status === "COMPLETED"
                              )
                              .sort((a, b) => {
                                // Sort to ensure REACHED_CUSTOMER_LOCATION comes first
                                if (
                                  a.delivery_partner_status ===
                                  "REACHED_CUSTOMER_LOCATION"
                                )
                                  return -1;
                                if (
                                  b.delivery_partner_status ===
                                  "REACHED_CUSTOMER_LOCATION"
                                )
                                  return 1;
                                return 0;
                              })
                              .map((item, index) => {
                                const isGarageReached =
                                  item.delivery_partner_status ===
                                  "REACHED_GARAGE_LOCATION";

                                // Get COMPLETED data if REACHED_GARAGE_LOCATION
                                const completedData = isGarageReached
                                  ? deliveryPartnerData.find(
                                      (d) =>
                                        d.delivery_partner_status ===
                                        "COMPLETED"
                                    )
                                  : null;

                                const displayStatus =
                                  item.delivery_partner_status ===
                                  "REACHED_CUSTOMER_LOCATION"
                                    ? "Reached PickUp Location"
                                    : orderDetails?.order_service_details
                                        ?.serviceType === "DISTANCE_BASED"
                                    ? "Reached DropOff Location"
                                    : "Completed";

                                const displayLocation = isGarageReached
                                  ? completedData?.location || item.location
                                  : item.location;

                                const latitude = isGarageReached
                                  ? completedData?.latitude || item.latitude
                                  : item.latitude;

                                const longitude = isGarageReached
                                  ? completedData?.longitude || item.longitude
                                  : item.longitude;

                                return (
                                  <tr key={index}>
                                    <td>{displayStatus}</td>
                                    <td>
                                      {latitude && longitude && (
                                        <a
                                          href={`https://www.google.com/maps?q=${latitude},${longitude}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            marginRight: "8px",
                                            textDecoration: "none",
                                          }}
                                          title="View on Google Maps"
                                        >
                                          📍
                                        </a>
                                      )}
                                      {displayLocation}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ImagePopupModal
        isOpen={showImagePopupModal}
        //  onClose={onCloseImagePopupModal}
        src={imageSource}
        onClose={() => setShowImagePopupModal(false)}
        message={""}
      />
    </div>
  );
};

export default VendorServiceDetails;
